import countryDomains from '@/assets/json/country_domains.json';

export default function useLiveSearchHelper() {
    async function openSearch(term, location, country, languageCode, view) {
        const isNews = view === 'timelineNews';
        const query = encodeURIComponent(term);
        const hl = languageCode;
        const countryCode = await getCountryCode(country);

        const uule = buildUule(location, country);
        const page = 1;
        const start = (page - 1) * 10;

        const cctld = await getGoogleDomain(countryCode);
        // No usable city -> omit &uule= entirely and let Google fall back to gl.
        // Same call the poller makes (impact-python build_uule returns None here):
        // Google ignores a uule it can't match, so sending a junk one buys nothing.
        const uuleParam = uule ? `&uule=${uule}` : '';
        const baseUrl = `https://www.${cctld}/search?q=${query}${uuleParam}&gl=${countryCode.toUpperCase()}&hl=${hl}`;
        const url = baseUrl + (isNews ? `&tbm=nws` : `&pws=0&start=${start}`);

        window.open(url, '_blank');
    }

    // 'israel-iw' -> 'israel'. Same regex the fleet and impact-python use
    // (serp_api_routing.strip_country_suffix / lambda_function.py:461).
    function stripCountrySuffix(country) {
        return String(country ?? '').replace(/-..$/, '');
    }

    // Python's str.title(), which is what the backend applies to the country name.
    // Must be \p{L} and not \w: \w is ASCII-only, so "côte d'ivoire" came out as
    // "CôTe D'Ivoire" (the accent ended a word) instead of "Côte D'Ivoire", and the
    // uule bytes then disagreed with the poller's.
    function titleCase(s) {
        return s.replace(/\p{L}+/gu, w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());
    }

    /**
     * The uule value (no '&uule=' prefix), or '' when there is no usable city.
     *
     * Ported from impact-python `api/services/serp_api_routing.py::build_uule`, which is
     * itself the poller's own recipe (DM-9568) — live search must ask Google for the same
     * place the poller did, or the page the user opens isn't the page we captured.
     *
     * `location` is terms.polllocation, and it is NOT "City CC": it is the bare city
     * everywhere except the USA, where the last token is the STATE:
     *   USA: "New York NY" -> "New York,NY,United States"
     *   UK:  "London"      -> "London,United Kingdom"   ('uk' title-cased would be 'Uk')
     *   else "Berlin"      -> "Berlin,Germany"
     * The previous version stripped the last space-delimited token unconditionally, so
     * every non-US location lost its last word — "Monaco"/"London"/"Berlin" collapsed to
     * an empty city and "Tel Aviv"/"Hong Kong" became "Tel"/"Hong" (DM-10288).
     */
    function buildUule(location, country) {
        // Coerce once, like the reference's `loc = str(polllocation)`. Without it a
        // non-string location threw on .lastIndexOf, and because CardHeader calls
        // openSearch fire-and-forget the throw became an unhandled rejection — the
        // logo click did nothing at all, with no window and no error.
        const loc = String(location ?? '');
        const city = loc.trim();
        if (!city) return '';

        const bareCountry = stripCountrySuffix(country).toLowerCase();
        const rawCountry = String(country ?? '').trim().toLowerCase();
        const lowerCity = city.toLowerCase();
        // A location that echoes the RAW suffixed country string ("israel-iw") is
        // junk, not a place — omit the uule and let Google fall back to gl/country.
        if (lowerCity === rawCountry && rawCountry !== bareCountry) {
            return '';
        }
        // A location that IS its country name is a city-state (Monaco) or a
        // country-level poll ("Israel"): a real Google location. The live poller can
        // omit the uule here because it polls through a geo-located proxy IP — but
        // live search runs in the user's browser with NO proxy, so gl alone can't pin
        // the place and Google geolocates by the user's IP instead (an Israeli user
        // opened a Monaco card and got Jerusalem, DM-10288). Encode the name as the
        // uule so Google targets the captured location regardless of the user's IP.
        if (lowerCity === bareCountry) {
            return generateUule(titleCase(city));
        }
        // Mojibake -> unusable as a Google location name. ('?' is how Impact stores
        // characters lost at write time; U+FFFD is how a bad decode surfaces here.)
        if (city.includes('?') || city.includes('�')) return '';

        // The RAW location is used from here (only the junk checks above trim) — the
        // live poller does not normalise the location either.
        let canonical;
        if (bareCountry === 'usa') {
            const lastSpaceIndex = loc.lastIndexOf(' ');
            const cityPart = lastSpaceIndex > 0 ? loc.substring(0, lastSpaceIndex) : '';
            const state = lastSpaceIndex > 0 ? loc.substring(lastSpaceIndex + 1) : '';
            canonical = cityPart ? `${cityPart},${state},United States` : `${loc},United States`;
        } else if (bareCountry === 'uk') {
            canonical = `${loc},United Kingdom`;
        } else {
            canonical = `${loc},${titleCase(bareCountry)}`;
        }

        return generateUule(canonical);
    }

    // Encoder only — mirrors fbutils Locations.generate_uule for every non-empty name.
    // (fbutils has an extra empty-name branch emitting 'F' + 'MTAwMDE='; unreachable
    // here because buildUule only calls this with a non-empty city / canonical name.)
    function generateUule(canonicalName) {
        const secretKeys = alphabetRange('A', 'Z').concat(
            alphabetRange('a', 'z'),
            numberRange(0, 9),
            ['-', '_']
        );
        const name = canonicalName.replace(/, /g, ',');
        // Code POINTS, matching Python's len(). String.length counts UTF-16 units, so
        // any astral character (emoji, rare CJK) shifted this index by one per surrogate
        // pair and picked a different key char than the poller for the same payload.
        const secretKey = secretKeys[[...name].length % secretKeys.length];
        return 'w+CAIQICI' + secretKey + btoa(encodeUtf8(name));
    }

    async function getGoogleDomain(countryCode) {
        try {
            const countries = countryDomains;
            let domain = 'google.com';

            for (let i = 0; i < countries.length; i++) {
                if (countryCode.toLowerCase() === countries[i].country_code.toLowerCase()) {
                    // Keep the google.com fallback when the row has no ccTLD. 29 of the
                    // 260 rows carry "domain": null (Monaco, Mayotte, Palau, ...) because
                    // Google has no ccTLD there; assigning it blind produced the literal
                    // URL https://www.null/search?... (DM-10288).
                    domain = countries[i].domain || 'google.com';
                    break;
                }
            }

            return domain;
        } catch (error) {
            console.error('Error fetching country domains:', error);
            return 'google.com';
        }
    }

    async function getCountryCode(country) {
        try {
            const countries = countryDomains;
            // pollcountry carries a language suffix ('france-fr', 'israel-iw', 'china-zh'),
            // which never matched the plain country names in the JSON — those terms fell
            // through to an empty gl= and google.com. Match on the bare country.
            const wanted = stripCountrySuffix(country).toLowerCase();
            let countryCode = '';

            for (let i = 0; i < countries.length; i++) {
                if (wanted === countries[i]?.country.toLowerCase()) {
                    countryCode = countries[i].country_code;
                    break;
                }
            }

            return countryCode;
        } catch (error) {
            console.error('Error fetching country domains:', error);
            return 'US';
        }
    }

    function alphabetRange(start, end) {
        return new Array(end.charCodeAt(0) + 1 - start.charCodeAt(0)).fill().map((d, i) => {
            return String.fromCharCode(i + start.charCodeAt(0));
        });
    }

    function numberRange(start, end) {
        return new Array(end + 1 - start).fill().map((d, i) => {
            return i + start;
        });
    }

    // btoa() only accepts code points 0-255, so hand it the UTF-8 BYTES of the name.
    // The old body (decodeURIComponent(encodeURIComponent(s))) was a no-op round trip:
    // accented cities encoded as latin-1 and disagreed with the poller's
    // base64.b64encode(name.encode()), and a non-latin-1 name threw InvalidCharacterError.
    function encodeUtf8(s) {
        return Array.from(new TextEncoder().encode(s), b => String.fromCharCode(b)).join('');
    }

    return {
        openSearch
    };
}
